import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Activity, ArrowUpRight, Backpack, Building2, Bus, CalendarDays, Camera, Check, FileCheck2, HeartPulse,
  Home, Link2, MapPin, Package, PhoneCall, Plus, Save, Search, ShieldCheck, Star, Trash2, UserRound,
  Utensils, X,
} from "lucide-react";
import { toast } from "sonner";
import { LoadingState, EmptyState, ErrorState } from "@/components/States";
import { Btn, Field, inputCls } from "@/components/Primitives";
import { SummitFormActions, SummitFormHeader } from "@/components/forms/SummitForm";
import DepartureField from "@/components/trip/DepartureField";
import { useAuth } from "@/context/AuthContext";
import { api, confirmAction, formatApiError, formatApiErrorDetail } from "@/lib/api";
import "./VendorsTab.css";

const steps = ["Vendor", "Provider", "Itinerary days", "Review"];
const serviceMeta = {
  transport: { label: "Transportation", noun: "transport", icon: Bus, color: "text-[#2D7A65]" },
  accommodation: { label: "Accommodation", noun: "accommodation", icon: Home, color: "text-[#2D7A65]" },
  other: { label: "Other vendor", noun: "vendor", icon: Building2, color: "text-[#2D7A65]" },
};
const logisticsVendorCategories = new Set(["transport", "accommodation"]);
const categoryMeta = {
  accommodation: { icon: Home, badge: "bg-[#E7F2ED] text-[#2D7A65] ring-[#BFD5C6]", pulse: "bg-[#2D7A65]" },
  transport: { icon: Bus, badge: "bg-[#EAF2FF] text-[#315D8A] ring-[#BED1EA]", pulse: "bg-[#315D8A]" },
  "equipment rental": { icon: Package, badge: "bg-[#F7F1E5] text-[#7A5D2D] ring-[#E6D7B9]", pulse: "bg-[#C8962D]" },
  catering: { icon: Utensils, badge: "bg-[#FFF1E7] text-[#A0522D] ring-[#F0C9B1]", pulse: "bg-[#D96B35]" },
  "local guide": { icon: UserRound, badge: "bg-[#E8F5EE] text-[#236D5C] ring-[#B7DEC9]", pulse: "bg-[#2D7A65]" },
  "permit provider": { icon: FileCheck2, badge: "bg-[#F0EDFF] text-[#5C4D9A] ring-[#D7CFF5]", pulse: "bg-[#6C5BAA]" },
  "medical support": { icon: HeartPulse, badge: "bg-[#FEEAEC] text-[#A33B4B] ring-[#F3C2CA]", pulse: "bg-[#D95366]" },
  photographer: { icon: Camera, badge: "bg-[#E7F7F8] text-[#24717B] ring-[#BEE1E4]", pulse: "bg-[#2A8C99]" },
  "activity provider": { icon: Activity, badge: "bg-[#EDF6E8] text-[#4F752B] ring-[#CBE0BC]", pulse: "bg-[#6E9E3B]" },
  porter: { icon: Backpack, badge: "bg-[#F2EFE8] text-[#65513B] ring-[#DBD0BE]", pulse: "bg-[#8A6A48]" },
  insurance: { icon: ShieldCheck, badge: "bg-[#E9F0FF] text-[#415E9B] ring-[#C6D5F5]", pulse: "bg-[#526EB0]" },
  other: { icon: Building2, badge: "bg-[#EEF1EF] text-[#516259] ring-[#D5DDD7]", pulse: "bg-[#516259]" },
};

function serviceFor(category) {
  return serviceMeta[category] || serviceMeta.other;
}
function isLogisticsVendorCategory(category) {
  return logisticsVendorCategories.has(String(category || "").toLowerCase());
}
function vendorCategoryMeta(category) {
  return categoryMeta[String(category || "").toLowerCase()] || categoryMeta.other;
}
function VendorCategoryBadge({ category, compact = false }) {
  const meta = vendorCategoryMeta(category);
  const Icon = meta.icon || Building2;
  return (
    <span className={`relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-2xl ring-1 ${meta.badge || categoryMeta.other.badge} ${compact ? "h-9 w-9" : "h-11 w-11"}`}>
      <Icon className={`${compact ? "h-4 w-4" : "h-5 w-5"} relative`} />
    </span>
  );
}

function phoneHref(value) {
  return `tel:${String(value || "").replace(/[^\d+]/g, "")}`;
}

function hasValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function RatingStars({ value }) {
  const rating = hasValue(value) ? Number(value) : null;
  if (rating === null || !Number.isFinite(rating)) return null;
  const rounded = Math.round(rating);
  return (
    <span className="flex items-center gap-1 text-xs font-semibold text-[#835B12]" aria-label={`Rating ${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((star) => <Star key={star} className={`h-3.5 w-3.5 ${star <= rounded ? "fill-[#EBC253] text-[#EBC253]" : "text-[#D8CFBD]"}`} />)}
      <span>{rating}/5</span>
    </span>
  );
}

function OrganizationVendorDetail({ vendor, onClose }) {
  const fields = [
    ["Region", vendor.region], ["Services", csv(vendor.service_types)], ["Address", vendor.address],
    ["Coordinates", vendor.latitude !== null && vendor.latitude !== undefined && vendor.longitude !== null && vendor.longitude !== undefined ? `${vendor.latitude}, ${vendor.longitude}` : ""],
    ["Contact name", vendor.contact_name], ["Phone", vendor.contact_phone], ["Email", vendor.contact_email],
    ["Capacity", vendor.capacity], ["Rating", vendor.rating !== null && vendor.rating !== undefined ? `${vendor.rating}/5` : ""],
    ["Charges", vendor.charges], ["Tags", csv(vendor.tags)],
  ].filter(([, value]) => hasValue(value));
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-[#091C18]/65 p-0 backdrop-blur-sm sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label={`${vendor.name} vendor details`}>
      <aside className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl border border-[#E2DFD7] bg-[#FFFBF3] p-5 shadow-2xl sm:rounded-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-[#E2DFD7] pb-4">
          <div className="flex min-w-0 items-start gap-3"><VendorCategoryBadge category={vendor.category} /><div className="min-w-0"><p className="text-xs font-black uppercase tracking-[0.12em] text-[#516259]">Organization vendor</p><h3 className="truncate text-xl font-black text-[#17211D]">{vendor.name}</h3><p className="text-sm capitalize text-slate-500">{[vendor.category, vendor.region].filter(hasValue).join(" · ")}</p></div></div>
          <button type="button" className="rounded-lg border border-[#E2DFD7] bg-white p-2 text-[#516259] hover:bg-[#F7F1E5]" onClick={onClose} aria-label="Close vendor details"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {fields.map(([label, value]) => <div key={label} className="rounded-lg border border-[#E2DFD7] bg-white p-3 text-sm"><p className="text-xs font-black uppercase tracking-[0.12em] text-[#516259]">{label}</p><p className="mt-1 break-words text-[#17211D]">{value}</p></div>)}
          {vendor.website && <div className="rounded-lg border border-[#E2DFD7] bg-white p-3 text-sm"><p className="text-xs font-black uppercase tracking-[0.12em] text-[#516259]">Website</p><a className="mt-1 block break-all text-[#2D7A65] hover:underline" href={vendor.website} target="_blank" rel="noreferrer">{vendor.website}</a></div>}
        </div>
        {vendor.details && <div className="mt-3 rounded-lg border border-[#E2DFD7] bg-white p-3 text-sm"><p className="text-xs font-black uppercase tracking-[0.12em] text-[#516259]">Details</p><p className="mt-1 whitespace-pre-wrap text-[#17211D]">{vendor.details}</p></div>}
        {vendor.pricing_notes && <div className="mt-3 rounded-lg border border-[#E2DFD7] bg-white p-3 text-sm"><p className="text-xs font-black uppercase tracking-[0.12em] text-[#516259]">Pricing notes</p><p className="mt-1 whitespace-pre-wrap text-[#17211D]">{vendor.pricing_notes}</p></div>}
        {vendor.internal_notes && <div className="mt-3 rounded-lg border border-[#E2DFD7] bg-[#F7F1E5] p-3 text-sm"><p className="text-xs font-black uppercase tracking-[0.12em] text-[#516259]">Internal notes</p><p className="mt-1 whitespace-pre-wrap text-[#17211D]">{vendor.internal_notes}</p></div>}
      </aside>
    </div>,
    document.body,
  );
}

const blank = {
  category: "transport",
  source: "",
  vendor_id: "",
  vendor_ids: [],
  vendor_search: "",
  vendor_region: "",
  itinerary_day_ids: [],
  assignment_type: "general",
  start_date: "",
  end_date: "",
  trip_price_minor: "",
  currency: "INR",
  trip_specific_notes: "",
  public_visibility: "private",
  status: "planned",
  provider_name: "",
  provider_region: "",
  provider_address: "",
  provider_website: "",
  provider_contact_phone: "",
  provider_contact_email: "",
  provider_charges: "",
  provider_rating: "",
  provider_details: "",
  add_to_org_vendor: false,
  create_separate_vendor: false,
  transport_type: "",
  direction: "local",
  origin: "",
  destination: "",
  departure_at: "",
  arrival_at: "",
  vehicle_details: "",
  booking_reference: "",
  accommodation_type: "lodging",
  check_in: "",
  check_out: "",
  room_type: "",
  room_count: "",
  confirmation_number: "",
};

function fresh(category = "transport", addToOrgDefault = false) {
  const safeCategory = isLogisticsVendorCategory(category) ? category : "transport";
  return { ...blank, category: safeCategory, assignment_type: safeCategory, add_to_org_vendor: addToOrgDefault };
}

function csv(value) {
  return Array.isArray(value) ? value.join(", ") : value || "";
}

function normalize(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function dayIds(record) {
  return record.itinerary_day_ids || (record.itinerary_day_id ? [record.itinerary_day_id] : []);
}

function dayTitle(day) {
  return `Day ${day.day_number}${day.title ? ` - ${day.title}` : ""}`;
}

function shortDayTitle(day) {
  return day ? `Day ${day.day_number}` : "Whole trip";
}

function formatWhen(value) {
  if (!value) return "";
  return String(value).replace("T", " ").slice(0, 16);
}

function assignmentNotes(form) {
  const detailParts = form.category === "transport"
    ? [
        form.transport_type && `Transport type: ${form.transport_type}`,
        (form.origin || form.destination) && `Route: ${form.origin || "origin not set"} to ${form.destination || "destination not set"}`,
        (form.departure_at || form.arrival_at) && `Timing: ${formatWhen(form.departure_at) || "departure not set"} to ${formatWhen(form.arrival_at) || "arrival not set"}`,
        form.vehicle_details && `Vehicle: ${form.vehicle_details}`,
        form.booking_reference && `Booking reference: ${form.booking_reference}`,
      ]
    : form.category === "accommodation"
      ? [
          form.accommodation_type && `Stay type: ${form.accommodation_type}`,
          (form.check_in || form.check_out) && `Stay: ${form.check_in || "check-in not set"} to ${form.check_out || "check-out not set"}`,
          form.room_type && `Room type: ${form.room_type}`,
          form.room_count && `Room count: ${form.room_count}`,
          form.confirmation_number && `Confirmation: ${form.confirmation_number}`,
        ]
      : [];
  return [...detailParts.filter(Boolean), form.trip_specific_notes].filter(Boolean).join("\n");
}

function assignmentPayload(form, dayId = null, vendorId = form.vendor_id) {
  return {
    batch_id: form.batch_id || "",
    vendor_id: vendorId,
    itinerary_day_id: dayId,
    activity_id: null,
    assignment_type: form.assignment_type || form.category || "general",
    start_date: form.start_date || null,
    end_date: form.end_date || null,
    trip_price_minor: form.trip_price_minor === "" ? null : Number(form.trip_price_minor),
    currency: form.currency || "INR",
    trip_specific_notes: assignmentNotes(form),
    public_visibility: form.public_visibility,
    status: form.status,
  };
}

function vendorPayload(form) {
  return {
    category: form.category,
    name: form.provider_name.trim(),
    region: form.provider_region,
    service_types: [form.category],
    website: form.provider_website,
    address: form.provider_address,
    latitude: null,
    longitude: null,
    contact_name: "",
    contact_phone: form.provider_contact_phone,
    contact_email: form.provider_contact_email,
    capacity: null,
    charges: form.provider_charges === "" ? null : Number(form.provider_charges),
    details: form.provider_details,
    pricing_notes: "",
    internal_notes: "",
    rating: form.provider_rating === "" ? null : Number(form.provider_rating),
    tags: [],
    active: true,
  };
}

function transportPayload(form) {
  return {
    batch_id: form.batch_id || "",
    type: form.transport_type.trim() || form.provider_name.trim(),
    direction: form.direction,
    origin_location_id: null,
    destination_location_id: null,
    departure_at: form.departure_at || null,
    arrival_at: form.arrival_at || null,
    provider: form.provider_name,
    booking_reference: form.booking_reference,
    ticket_url: form.provider_website,
    vehicle_details: form.vehicle_details,
    class_or_seat: "",
    cost_minor: form.trip_price_minor === "" ? null : Number(form.trip_price_minor),
    currency: form.currency || "INR",
    status: form.status,
    notes: assignmentNotes(form),
    visibility: form.public_visibility === "private" ? "operator" : form.public_visibility,
    itinerary_day_ids: form.itinerary_day_ids,
  };
}

function accommodationPayload(form) {
  return {
    batch_id: form.batch_id || "",
    property_name: form.provider_name.trim(),
    type: form.accommodation_type,
    address: form.provider_address,
    latitude: null,
    longitude: null,
    check_in: form.check_in || form.start_date || null,
    check_out: form.check_out || form.end_date || null,
    provider: form.provider_name,
    confirmation_number: form.confirmation_number,
    room_type: form.room_type,
    room_count: form.room_count === "" ? null : Number(form.room_count),
    contact_details: form.provider_contact_phone ? { primary: form.provider_contact_phone } : {},
    website: form.provider_website,
    amenities: csv(form.provider_details).split(",").map((item) => item.trim()).filter(Boolean),
    cost_minor: form.trip_price_minor === "" ? null : Number(form.trip_price_minor),
    currency: form.currency || "INR",
    status: form.status,
    notes: assignmentNotes(form),
    visibility: form.public_visibility === "private" ? "operator" : form.public_visibility,
    itinerary_day_ids: form.itinerary_day_ids,
  };
}

function StepButton({ index, active, complete, label }) {
  return (
    <div className={`flex min-w-0 items-center gap-2 rounded-full px-3 py-2 text-xs font-black ${active ? "bg-[#EBC253] text-[#17211D]" : complete ? "bg-emerald-50 text-emerald-800" : "bg-white/10 text-white/70"}`}>
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/80 text-[11px] text-[#17211D]">{complete ? <Check className="h-3 w-3" /> : index + 1}</span>
      <span className="hidden truncate sm:inline">{label}</span>
    </div>
  );
}

export default function VendorsTab({ trip, canEdit }) {
  const { perms = [] } = useAuth();
  const canManageOrgVendors = ["org:manage", "trip:edit"].some((permission) => perms.includes(permission));
  const [assignments, setAssignments] = useState(null);
  const [transportation, setTransportation] = useState([]);
  const [accommodations, setAccommodations] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [days, setDays] = useState([]);
  const [form, setForm] = useState(fresh("transport", canManageOrgVendors));
  const [step, setStep] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [managingAssignments, setManagingAssignments] = useState(null);
  const [selectedVendorDetail, setSelectedVendorDetail] = useState(null);
  const [error, setError] = useState("");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [dayFilter, setDayFilter] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const [assignmentResp, vendorResp, dayResp, transportResp, accommodationResp] = await Promise.all([
        api.get(`/trips/${trip.id}/vendor-assignments`),
        api.get("/org/vendors").catch(() => ({ data: { data: [] } })),
        api.get(`/trips/${trip.id}/days`),
        api.get(`/trips/${trip.id}/transportation`).catch(() => ({ data: { data: [] } })),
        api.get(`/trips/${trip.id}/accommodations`).catch(() => ({ data: { data: [] } })),
      ]);
      setAssignments((assignmentResp.data.data || []).filter((assignment) => isLogisticsVendorCategory(assignment.vendor?.category)));
      setVendors((vendorResp.data.data || []).filter((vendor) => isLogisticsVendorCategory(vendor.category)));
      setDays(dayResp.data.data || []);
      setTransportation(transportResp.data.data || []);
      setAccommodations(accommodationResp.data.data || []);
    } catch (err) {
      setError(formatApiError(err, "Could not load trip logistics."));
    }
  }, [trip.id]);
  useEffect(() => { load(); }, [load]);

  const addToOrgDefault = canManageOrgVendors;
  const vendorOptions = useMemo(() => {
    const map = new globalThis.Map((vendors || []).map((vendor) => [vendor.id, vendor]));
    if (editing?.vendor && editing.vendor_id && !map.has(editing.vendor_id)) {
      map.set(editing.vendor_id, editing.vendor);
    }
    return [...map.values()];
  }, [editing, vendors]);

  const selectedVendors = vendorOptions.filter((vendor) => form.vendor_ids.includes(vendor.id));
  const selectedVendor = vendorOptions.find((vendor) => vendor.id === form.vendor_id) || selectedVendors[0] || editing?.vendor || null;
  const filteredVendors = vendorOptions.filter((vendor) => {
    if (!isLogisticsVendorCategory(vendor.category)) return false;
    const text = [
      vendor.name,
      vendor.region,
      vendor.address,
      vendor.category,
      csv(vendor.service_types),
      vendor.details,
      vendor.pricing_notes,
      vendor.internal_notes,
      vendor.tags,
      vendor.charges,
      vendor.rating,
      vendor.contact_name,
      vendor.contact_phone,
      vendor.contact_email,
    ].join(" ").toLowerCase();
    if (form.vendor_search && !text.includes(form.vendor_search.toLowerCase())) return false;
    return true;
  });
  const vendorRegionFilter = normalize(form.vendor_region);
  const suggestedVendorRegion = normalize(form.vendor_region);
  const visibleProviderVendors = filteredVendors.filter((vendor) => {
    const region = suggestedVendorRegion;
    if (!region) return true;
    return normalize(vendor.region).includes(region) || normalize(vendor.address).includes(region);
  });
  const providerVendorList = visibleProviderVendors.length || vendorRegionFilter ? visibleProviderVendors : filteredVendors;
  const duplicateMatches = vendorOptions.filter((vendor) => {
    if (!isLogisticsVendorCategory(vendor.category) || vendor.category !== form.category) return false;
    const name = normalize(form.provider_name);
    if (!name) return false;
    const vendorName = normalize(vendor.name);
    const sameName = vendorName.includes(name) || name.includes(vendorName);
    const sameRegion = normalize(form.provider_region) && normalize(vendor.region).includes(normalize(form.provider_region));
    const samePhone = form.provider_contact_phone && String(vendor.contact_phone || "").includes(form.provider_contact_phone);
    const sameWebsite = form.provider_website && normalize(vendor.website) === normalize(form.provider_website);
    const sameAddress = form.provider_address && normalize(vendor.address).includes(normalize(form.provider_address));
    return sameName || samePhone || sameWebsite || (sameRegion && sameAddress);
  }).slice(0, 3);

  const allLogistics = [
    ...(assignments || []).map((item) => ({ kind: item.vendor?.category || "vendor", source: "assignment", id: item.id, title: item.vendor?.name || "Vendor", subtitle: item.assignment_type || item.vendor?.category, visibility: item.public_visibility, days: dayIds(item), notes: item.trip_specific_notes, record: item })),
    ...transportation.map((item) => ({
      kind: "transport",
      source: "trip",
      id: item.id,
      title: item.type,
      subtitle: item.provider || "Trip-only transport",
      visibility: item.visibility,
      days: dayIds(item),
      notes: [item.departure_at && `Departure: ${formatWhen(item.departure_at)}`, item.arrival_at && `Arrival: ${formatWhen(item.arrival_at)}`, item.notes].filter(hasValue).join("\n"),
      record: item,
    })),
    ...accommodations.map((item) => ({
      kind: "accommodation",
      source: "trip",
      id: item.id,
      title: item.property_name,
      subtitle: item.provider || "Trip-only accommodation",
      visibility: item.visibility,
      days: dayIds(item),
      notes: [item.check_in && `Check-in: ${item.check_in}`, item.check_out && `Check-out: ${item.check_out}`, item.notes].filter(hasValue).join("\n"),
      record: item,
    })),
  ];
  const visibleLogistics = allLogistics.filter((item) => {
    if (serviceFilter !== "all" && item.kind !== serviceFilter) return false;
    if (dayFilter && !item.days.includes(dayFilter)) return false;
    const text = [item.title, item.subtitle, item.notes, item.visibility, item.kind].join(" ").toLowerCase();
    if (search && !text.includes(search.toLowerCase())) return false;
    return true;
  });
  const visibleOrganizationAssignments = visibleLogistics.filter((item) => item.source === "assignment");
  const visibleTripOnlyLogistics = visibleLogistics.filter((item) => item.source === "trip");
  const organizationVendorsById = new globalThis.Map(vendors.map((vendor) => [vendor.id, vendor]));
  const organizationVendorGroups = Object.values(visibleOrganizationAssignments.reduce((groups, item) => {
    const vendor = { ...(item.record.vendor || {}), ...(organizationVendorsById.get(item.record.vendor_id) || {}) };
    const key = item.record.vendor_id || vendor.id || item.id;
    if (!groups[key]) groups[key] = { key, vendor, assignments: [] };
    groups[key].assignments.push(item);
    return groups;
  }, {}));

  const formWarnings = [
    form.public_visibility === "public" && !form.trip_specific_notes ? "Public visibility is selected but participant-facing details are sparse." : "",
    form.source === "new" && duplicateMatches.length > 0 && !form.create_separate_vendor ? "A similar organization vendor may already exist." : "",
  ].filter(Boolean);

  const dayLabel = (id) => {
    const day = days.find((item) => item.id === id);
    return day ? dayTitle(day) : "Whole trip";
  };
  const dayList = (ids) => ids.length ? ids.map(dayLabel).join(", ") : "Whole trip";

  const openWizard = (category) => {
    setEditing(null);
    setForm({ ...fresh(category, addToOrgDefault), itinerary_day_ids: days.map((day) => day.id) });
    setStep(0);
    setModalOpen(true);
  };
  const closeWizard = () => {
    setEditing(null);
    setForm(fresh("transport", addToOrgDefault));
    setStep(0);
    setModalOpen(false);
  };
  const openVendorDetails = async (vendor) => {
    setSelectedVendorDetail(vendor);
    if (!vendor?.id) return;
    try {
      const { data } = await api.get(`/org/vendors/${vendor.id}`);
      setSelectedVendorDetail(data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load vendor details."));
    }
  };
  const changeService = (category) => {
    const safeCategory = isLogisticsVendorCategory(category) ? category : "transport";
    setForm({ ...form, category: safeCategory, assignment_type: safeCategory });
  };
  const toggleExistingVendor = (vendor) => {
    const selected = new Set(form.vendor_ids);
    if (selected.has(vendor.id)) selected.delete(vendor.id);
    else selected.add(vendor.id);
    const vendorIds = editing?.type === "assignment" ? [vendor.id] : [...selected];
    const primaryVendor = vendorIds.length ? vendorOptions.find((item) => item.id === vendorIds[0]) || vendor : null;
    const primaryCategory = isLogisticsVendorCategory(primaryVendor?.category) ? primaryVendor.category : form.category;
    setForm({
      ...form,
      source: "existing",
      category: primaryCategory,
      assignment_type: primaryCategory,
      vendor_id: primaryVendor?.id || "",
      vendor_ids: vendorIds,
      provider_name: primaryVendor?.name || "",
      provider_region: primaryVendor?.region || "",
      provider_address: primaryVendor?.address || "",
      provider_website: primaryVendor?.website || "",
      provider_contact_phone: primaryVendor?.contact_phone || "",
      provider_contact_email: primaryVendor?.contact_email || "",
      provider_charges: primaryVendor?.charges ?? "",
      provider_rating: primaryVendor?.rating ?? "",
      provider_details: primaryVendor?.details || "",
      trip_price_minor: form.trip_price_minor || primaryVendor?.charges || "",
    });
  };
  const applyDuplicateVendor = (vendor) => {
    setForm({
      ...form,
      source: "existing",
      category: isLogisticsVendorCategory(vendor.category) ? vendor.category : form.category,
      assignment_type: isLogisticsVendorCategory(vendor.category) ? vendor.category : form.category,
      vendor_id: vendor.id,
      vendor_ids: [vendor.id],
      provider_name: vendor.name,
      provider_region: vendor.region || "",
      provider_address: vendor.address || "",
      provider_website: vendor.website || "",
      provider_contact_phone: vendor.contact_phone || "",
      provider_contact_email: vendor.contact_email || "",
      provider_charges: vendor.charges ?? "",
      provider_rating: vendor.rating ?? "",
      provider_details: vendor.details || "",
      trip_price_minor: form.trip_price_minor || vendor.charges || "",
    });
    setStep(1);
  };
  const toggleDay = (id) => {
    const set = new Set(form.itinerary_day_ids);
    if (set.has(id)) set.delete(id); else set.add(id);
    setForm({ ...form, itinerary_day_ids: [...set] });
  };
  const selectAllDays = () => setForm({ ...form, itinerary_day_ids: days.map((day) => day.id) });
  const editAssignment = (assignment) => {
    const category = isLogisticsVendorCategory(assignment.vendor?.category) ? assignment.vendor.category : "transport";
    setEditing({ type: "assignment", record: assignment });
    setForm({
      ...fresh(category, addToOrgDefault),
      source: "existing",
      vendor_id: assignment.vendor_id,
      batch_id: assignment.batch_id || "",
      vendor_ids: [assignment.vendor_id],
      provider_name: assignment.vendor?.name || "",
      provider_region: assignment.vendor?.region || "",
      assignment_type: assignment.assignment_type || assignment.vendor?.category || category,
      start_date: assignment.start_date || "",
      end_date: assignment.end_date || "",
      trip_price_minor: assignment.trip_price_minor ?? "",
      currency: assignment.currency || "INR",
      trip_specific_notes: assignment.trip_specific_notes || "",
      public_visibility: assignment.public_visibility || "private",
      status: assignment.status || "planned",
      itinerary_day_ids: dayIds(assignment),
    });
    setStep(2);
    setModalOpen(true);
  };
  const duplicateAssignment = (assignment) => {
    editAssignment(assignment);
    setEditing(null);
  };
  const editTripOnly = (kind, record) => {
    const isTransport = kind === "transport";
    setEditing({ type: "trip", kind, record });
    setForm({
      ...fresh(kind, false),
      source: "new",
      add_to_org_vendor: false,
      batch_id: record.batch_id || "",
      provider_name: record.provider || record.property_name || record.type || "",
      provider_address: record.address || "",
      provider_website: record.website || record.ticket_url || "",
      provider_contact_phone: record.contact_details?.primary || "",
      assignment_type: kind,
      start_date: record.start_date || record.check_in || "",
      end_date: record.end_date || record.check_out || "",
      trip_price_minor: record.cost_minor ?? "",
      currency: record.currency || "INR",
      trip_specific_notes: record.notes || "",
      public_visibility: record.visibility || "operator",
      status: record.status || "planned",
      itinerary_day_ids: dayIds(record),
      transport_type: isTransport ? record.type || "" : "",
      direction: record.direction || "local",
      departure_at: record.departure_at || "",
      arrival_at: record.arrival_at || "",
      vehicle_details: record.vehicle_details || "",
      booking_reference: record.booking_reference || "",
      accommodation_type: !isTransport ? record.type || "lodging" : "lodging",
      check_in: record.check_in || "",
      check_out: record.check_out || "",
      room_type: record.room_type || "",
      room_count: record.room_count ?? "",
      confirmation_number: record.confirmation_number || "",
    });
    setStep(2);
    setModalOpen(true);
  };
  const duplicateTripOnly = (kind, record) => {
    editTripOnly(kind, record);
    setEditing(null);
  };
  const suggestDays = () => {
    if (form.category === "accommodation") {
      const start = form.check_in || form.start_date;
      const end = form.check_out || form.end_date;
      if (start && end) {
        const ids = days.filter((day) => {
          const date = String(day.date || "").slice(0, 10);
          return date && date >= start && date <= end;
        }).map((day) => day.id);
        if (ids.length) return setForm({ ...form, itinerary_day_ids: ids });
      }
    }
    if (form.category === "transport") {
      const query = normalize(`${form.origin} ${form.destination}`);
      const ids = days.filter((day) => normalize(`${day.title} ${day.summary} ${day.description}`).split(" ").some((word) => word && query.includes(word))).map((day) => day.id);
      if (ids.length) return setForm({ ...form, itinerary_day_ids: ids });
    }
    toast.info("No clear day suggestion found. Choose manually.");
  };

  const canGoNext = () => {
    if (step === 0) return !!form.source;
    if (step === 1) return form.source === "existing" ? form.vendor_ids.length > 0 || !!form.vendor_id : !!form.provider_name.trim();
    if (step === 2) return form.itinerary_day_ids.length > 0;
    return true;
  };
  const next = () => {
    if (!canGoNext()) {
      toast.error(step === 1 ? "Choose or create a provider first." : step === 2 ? "Select at least one itinerary day." : "Complete this step first.");
      return;
    }
    setStep(Math.min(step + 1, steps.length - 1));
  };
  const back = () => setStep(Math.max(step - 1, 0));

  const saveExistingAssignment = async () => {
    const targetDays = form.itinerary_day_ids;
    if (editing?.type === "assignment") {
      await api.put(`/vendor-assignments/${editing.record.id}`, assignmentPayload(form, targetDays[0], form.vendor_id));
      await Promise.all(targetDays.slice(1).map((dayId) => api.post(`/trips/${trip.id}/vendor-assignments`, assignmentPayload(form, dayId, form.vendor_id))));
      return true;
    }
    const vendorIds = form.vendor_ids.length ? form.vendor_ids : [form.vendor_id].filter(Boolean);
    await Promise.all(vendorIds.flatMap((vendorId) => {
      const vendor = vendorOptions.find((item) => item.id === vendorId);
      const category = isLogisticsVendorCategory(vendor?.category) ? vendor.category : form.category || "transport";
      const assignmentForm = { ...form, category, assignment_type: category };
      return targetDays.map((dayId) => api.post(`/trips/${trip.id}/vendor-assignments`, assignmentPayload(assignmentForm, dayId, vendorId)));
    }));
    return true;
  };
  const saveNewProvider = async () => {
    if (editing?.type === "trip") {
      if (editing.kind === "transport") await api.put(`/transportation/${editing.record.id}`, transportPayload(form));
      else await api.put(`/accommodations/${editing.record.id}`, accommodationPayload(form));
      return true;
    }
    if (form.add_to_org_vendor && canManageOrgVendors) {
      const { data: vendor } = await api.post("/org/vendors", vendorPayload(form));
      const targetDays = form.itinerary_day_ids;
      await Promise.all(targetDays.map((dayId) => api.post(`/trips/${trip.id}/vendor-assignments`, assignmentPayload({ ...form, vendor_id: vendor.id }, dayId))));
      return true;
    }
    if (form.category === "transport") {
      await api.post(`/trips/${trip.id}/transportation`, transportPayload(form));
      return true;
    }
    if (form.category === "accommodation") {
      await api.post(`/trips/${trip.id}/accommodations`, accommodationPayload(form));
      return true;
    }
    toast.error("Use Settings or the vendor master list for non-logistics vendors.");
    return false;
  };
  const save = async (e) => {
    e.preventDefault();
    if (!canEdit) return toast.error("You do not have permission to manage trip logistics.");
    if (form.itinerary_day_ids.length === 0) return toast.error("Select at least one itinerary day before saving.");
    if (form.source === "existing" && form.vendor_ids.length === 0 && !form.vendor_id) return toast.error("Select at least one vendor.");
    if (form.source === "new" && !form.provider_name.trim()) return toast.error("Provider name is required.");
    if (form.source === "new" && duplicateMatches.length > 0 && !form.create_separate_vendor && form.add_to_org_vendor) {
      toast.error("Resolve the possible duplicate before saving to organization vendors.");
      return;
    }
    try {
      const saved = form.source === "existing" ? await saveExistingAssignment() : await saveNewProvider();
      if (!saved) return;
      toast.success(`${serviceFor(form.category).label} saved`);
      closeWizard();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const removeAssignment = async (assignment) => {
    if (!confirmAction("Remove this vendor assignment from the trip?")) return;
    try {
      await api.delete(`/vendor-assignments/${assignment.id}`);
      toast.success("Vendor assignment removed");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const removeTripOnly = async (kind, record) => {
    if (!confirmAction(`Delete this trip-only ${kind} record?`)) return;
    try {
      if (kind === "transport") await api.delete(`/transportation/${record.id}`);
      else await api.delete(`/accommodations/${record.id}`);
      toast.success(`${serviceFor(kind).label} removed`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (assignments === null) return <LoadingState label="Loading logistics and vendors..." />;

  const activeMeta = serviceFor(form.category);
  const hasFilters = Boolean(search || serviceFilter !== "all" || dayFilter);
  const clearFilters = () => { setSearch(""); setServiceFilter("all"); setDayFilter(""); };

  return (
    <div className="tp-vendors" data-testid="trip-vendor-assignments">
      <section className="tp-vendors-panel" aria-label="Trip vendors and logistics">
        <div className="tp-vendors-header">
          <div>
            <p className="tp-vendors-eyebrow">TRIP LOGISTICS</p>
            <h2>Transport & stays</h2>
            <p className="tp-vendors-intro">Your providers, service details and day-by-day assignments.</p>
          </div>
          {canEdit && (
            <Btn type="button" className="tp-vendors-add" onClick={() => openWizard("transport")}><Plus className="h-4 w-4" /> Assign logistics</Btn>
          )}
        </div>

        <div className="tp-vendors-content">
            <div className="tp-vendors-filters">
              <Field label="Search"><span className="tp-vendors-search"><Search aria-hidden="true" /><input className={inputCls} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Vendor, route, stay or notes" /></span></Field>
              <Field label="Service"><select className={inputCls} value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}><option value="all">All services</option><option value="transport">Transport</option><option value="accommodation">Accommodation</option></select></Field>
              <Field label="Day"><select className={inputCls} value={dayFilter} onChange={(e) => setDayFilter(e.target.value)}><option value="">All days</option>{days.map((day) => <option key={day.id} value={day.id}>{dayTitle(day)}</option>)}</select></Field>
            </div>
            {hasFilters && <div className="tp-vendors-filter-summary"><span role="status">{visibleLogistics.length} matching assignment{visibleLogistics.length === 1 ? "" : "s"}</span><button type="button" onClick={clearFilters}><X className="h-3.5 w-3.5" /> Clear filters</button></div>}
            <div className="space-y-5">
              {organizationVendorGroups.length > 0 && (
                <div>
                  <div className="tp-vendors-section-heading">
                    <h3>Trip vendors <span>{organizationVendorGroups.length}</span></h3>
                    <p>Connected to your organization</p>
                  </div>
                  <div className="tp-vendors-grid">
                    {organizationVendorGroups.map(({ key, vendor, assignments }) => {
                      const primaryAssignment = assignments[0];
                      const assignmentCount = assignments.length;
                      const coveredDayIds = [...new Set(assignments.flatMap((assignment) => assignment.days))].sort((a, b) => days.findIndex((day) => day.id === a) - days.findIndex((day) => day.id === b));
                      const coversEntireTrip = days.length > 0 && coveredDayIds.length === days.length;
                      const hasLocation = hasValue(vendor.region) || hasValue(vendor.address);
                      const hasRating = hasValue(vendor.rating) && Number.isFinite(Number(vendor.rating));
                      const hasCharges = hasValue(vendor.charges);
                      const hasDetails = hasValue(vendor.details);
                      const category = vendor.category || primaryAssignment.kind;
                      const extraServices = csv(vendor.service_types).split(",").map((service) => service.trim()).filter((service) => hasValue(service) && ![normalize(category), normalize(serviceFor(category).label)].includes(normalize(service))).join(", ");
                      const hasBody = hasLocation || hasRating || hasCharges || hasDetails || hasValue(extraServices);
                      return (
                        <article key={`vendor-${key}`} className={`tp-vendor-card ${vendor.active === false ? "tp-vendor-card-inactive" : ""}`}>
                          <div className="tp-vendor-card-heading">
                            <div className="flex min-w-0 items-start gap-3">
                              <VendorCategoryBadge category={vendor.category || primaryAssignment.kind} />
                              <div className="min-w-0">
                                <p className="tp-vendor-category">{serviceFor(vendor.category || primaryAssignment.kind).label}</p>
                                <h4>{vendor.name || primaryAssignment.title}</h4>
                              </div>
                            </div>
                            <span className="tp-vendor-status"><span />{vendor.active === false ? "Inactive" : "Active"}</span>
                          </div>
                          {hasBody && <div className="tp-vendor-card-body">
                          {hasLocation && <div className="tp-vendor-location">
                            <MapPin aria-hidden="true" />
                            <div>{hasValue(vendor.region) && <p>{vendor.region}</p>}{hasValue(vendor.address) && <p>{vendor.address}</p>}</div>
                          </div>}
                          {(hasRating || hasCharges) && <dl className="tp-vendor-facts">
                            {hasRating && <div><dt>Vendor rating</dt><dd><RatingStars value={vendor.rating} /></dd></div>}
                            {hasCharges && <div><dt>Charges</dt><dd className="tp-vendor-charge">{Number.isFinite(Number(vendor.charges)) ? Number(vendor.charges).toLocaleString() : vendor.charges}</dd></div>}
                          </dl>}
                          {(extraServices || hasDetails) && <div className="tp-vendor-services">
                            {extraServices && <p><span>Services</span> {extraServices}</p>}
                            {hasDetails && <p className="tp-vendor-description">{vendor.details}</p>}
                          </div>}
                          </div>}
                          <div className="tp-vendor-assignment">
                            <div className="tp-vendor-assignment-heading">
                              <p><CalendarDays aria-hidden="true" /> Trip coverage</p>
                              <span>{assignmentCount} assignment{assignmentCount === 1 ? "" : "s"}</span>
                            </div>
                            <div className="tp-vendor-days">
                              {coveredDayIds.length === 0 ? <span>Whole trip</span> : coversEntireTrip ? <span>All itinerary days</span> : coveredDayIds.map((dayId) => <span key={dayId} title={dayLabel(dayId)}>{shortDayTitle(days.find((day) => day.id === dayId))}</span>)}
                            </div>
                          </div>
                          <div className="tp-vendor-actions">
                            <button type="button" className="tp-vendor-detail-link" onClick={() => openVendorDetails(vendor)}>View details <ArrowUpRight aria-hidden="true" /></button>
                            <div>
                              {vendor.contact_phone && <a href={phoneHref(vendor.contact_phone)} className="tp-vendor-call" aria-label={`Call ${vendor.name}`} title={`Call ${vendor.contact_phone}`}><PhoneCall className="h-4 w-4" /></a>}
                              {canEdit && <Btn type="button" variant="secondary" className="tp-vendor-manage" onClick={() => setManagingAssignments({ vendor, title: vendor.name || primaryAssignment.title, assignments })}>Manage assignments</Btn>}
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              )}

              {visibleTripOnlyLogistics.length > 0 && (
                <div>
                  <div className="tp-vendors-section-heading">
                    <h3>Trip-only logistics <span>{visibleTripOnlyLogistics.length}</span></h3>
                    <p>Arranged just for this trip</p>
                  </div>
                  <div className="grid gap-2">
                    {visibleTripOnlyLogistics.map((item) => (
                      <div key={`trip-${item.kind}-${item.id}`} className="tp-vendor-trip-record">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div className="flex min-w-0 items-start gap-3">
                            <VendorCategoryBadge category={item.kind} compact />
                            <div className="min-w-0"><p className="break-words font-semibold">{item.title}</p><p className="text-sm text-slate-500">{item.subtitle} · {dayList(item.days)}</p>{item.notes && <p className="mt-2 whitespace-pre-wrap break-words text-sm text-slate-600">{item.notes}</p>}</div>
                          </div>
                          {canEdit && <div className="flex shrink-0 gap-1"><button type="button" className="rounded-md border border-[#E2DFD7] bg-white px-2 py-1 text-xs font-semibold text-[#516259] hover:bg-[#F7F1E5]" onClick={() => editTripOnly(item.kind, item.record)}>Edit</button><button type="button" className="rounded-md border border-[#E2DFD7] bg-white px-2 py-1 text-xs font-semibold text-[#516259] hover:bg-[#F7F1E5]" onClick={() => duplicateTripOnly(item.kind, item.record)}>Duplicate</button><button type="button" className="rounded-md border border-[#E2DFD7] bg-white p-1 text-slate-400 hover:text-rose-600" aria-label={`Remove ${item.title}`} onClick={() => removeTripOnly(item.kind, item.record)}><Trash2 className="h-4 w-4" /></button></div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {organizationVendorGroups.length === 0 && visibleTripOnlyLogistics.length === 0 && <EmptyState title={hasFilters ? "No matching logistics" : "Your trip's partners start here"} description={hasFilters ? "Try another vendor, service or itinerary day." : "Assign transport and accommodation to keep every trip detail in one place."} />}
            </div>
        </div>
      </section>

      {selectedVendorDetail && <OrganizationVendorDetail vendor={selectedVendorDetail} onClose={() => setSelectedVendorDetail(null)} />}

      {managingAssignments && createPortal(
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-[#091C18]/65 p-0 backdrop-blur-sm sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label={`Manage ${managingAssignments.title} assignments`}>
          <aside className="w-full max-w-xl rounded-t-2xl border border-[#E2DFD7] bg-[#FFFBF3] p-5 shadow-2xl sm:rounded-2xl">
            <div className="flex items-start justify-between gap-3 border-b border-[#E2DFD7] pb-4">
              <div className="flex min-w-0 items-center gap-3">
                <VendorCategoryBadge category={managingAssignments.vendor.category} compact />
                <div><p className="text-xs font-black uppercase tracking-[0.12em] text-[#516259]">Trip assignments</p><h3 className="truncate text-lg font-black text-[#17211D]">{managingAssignments.title}</h3></div>
              </div>
              <button type="button" className="rounded-lg border border-[#E2DFD7] bg-white p-2 text-[#516259] hover:bg-[#F7F1E5]" onClick={() => setManagingAssignments(null)} aria-label="Close assignment manager"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 space-y-2">
              {managingAssignments.assignments.map((assignment) => (
                <div key={assignment.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#E2DFD7] bg-white p-3">
                  <div><p className="font-semibold text-[#17211D]">{dayList(assignment.days)}</p>{hasValue(assignment.record.trip_specific_notes) && <p className="mt-0.5 whitespace-pre-wrap text-sm text-slate-500">{assignment.record.trip_specific_notes}</p>}</div>
                  <div className="flex gap-2"><Btn type="button" variant="secondary" onClick={() => { setManagingAssignments(null); editAssignment(assignment.record); }}>Edit</Btn><Btn type="button" variant="secondary" onClick={() => { setManagingAssignments(null); duplicateAssignment(assignment.record); }}>Duplicate</Btn><button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#E2DFD7] bg-white text-slate-400 hover:text-rose-600" aria-label={`Remove ${managingAssignments.title} from this trip`} onClick={() => removeAssignment(assignment.record)}><Trash2 className="h-4 w-4" /></button></div>
                </div>
              ))}
            </div>
          </aside>
        </div>,
        document.body,
      )}

      {modalOpen && createPortal(
        <div className="summit-form-overlay vendor-form-overlay">
          <form onSubmit={save} className="summit-form summit-form--sheet max-w-5xl">
            <SummitFormHeader eyebrow={editing ? "Update logistics" : "New logistics"} title={`${editing ? "Edit" : "Add"} ${activeMeta.noun}`} description="Set up transport or accommodation and assign it to the right days." onClose={closeWizard}>
              <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
                {steps.map((label, index) => <StepButton key={label} index={index} label={label} active={step === index} complete={step > index} />)}
              </div>
            </SummitFormHeader>
            <div className="px-6 pt-3"><DepartureField trip={trip} value={form.batch_id} onChange={(batch_id) => setForm({ ...form, batch_id })} /></div>

            <div className="summit-form__body">
              {step === 0 && (
                <div className="space-y-3">
                  <h3 className="text-lg font-black text-[#17211D]">How would you like to add it?</h3>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <button type="button" className={`rounded-xl border p-4 text-left ${form.source === "existing" ? "border-[#2D7A65] bg-[#2D7A65]/10" : "border-[#E2DFD7] bg-white hover:bg-[#F7F1E5]"}`} onClick={() => setForm({ ...form, source: "existing" })}>
                      <p className="font-semibold">Use saved logistics provider</p>
                      <p className="mt-1 text-sm text-slate-500">Select saved transport or accommodation providers, then assign them to itinerary days.</p>
                    </button>
                    <button type="button" className={`rounded-xl border p-4 text-left ${form.source === "new" ? "border-[#2D7A65] bg-[#2D7A65]/10" : "border-[#E2DFD7] bg-white hover:bg-[#F7F1E5]"}`} onClick={() => setForm({ ...form, source: "new" })}>
                      <p className="font-semibold">Create new logistics provider</p>
                      <p className="mt-1 text-sm text-slate-500">Add a provider for this trip and optionally save it for reuse.</p>
                    </button>
                  </div>
                </div>
              )}

              {step === 1 && form.source === "existing" && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-black text-[#17211D]">Choose providers</h3>
                    <p className="text-sm text-slate-500">
                      {editing?.type === "assignment"
                        ? "Choose one replacement provider for this assignment."
                        : "Select one or more saved vendors. Every selected vendor will be assigned to the same itinerary days."}
                    </p>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_16rem]">
                    <Field label="Search vendors"><input className={inputCls} value={form.vendor_search} onChange={(e) => setForm({ ...form, vendor_search: e.target.value })} placeholder="Name, address, service, charges" /></Field>
                    <Field label="Region"><input className={inputCls} value={form.vendor_region} onChange={(e) => setForm({ ...form, vendor_region: e.target.value })} placeholder="Filter by region" /></Field>
                  </div>

                  {selectedVendors.length > 0 && (
                    <div className="rounded-xl border border-[#2D7A65] bg-[#2D7A65]/10 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs font-black uppercase tracking-[0.14em] text-[#516259]">Selected vendors ({selectedVendors.length})</p>
                        {editing?.type !== "assignment" && <button type="button" className="text-xs font-bold text-[#2D7A65] hover:underline" onClick={() => setForm({ ...form, vendor_id: "", vendor_ids: [], provider_name: "", provider_region: "", provider_address: "", provider_website: "", provider_contact_phone: "", provider_contact_email: "", provider_charges: "", provider_rating: "", provider_details: "" })}>Clear all</button>}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {selectedVendors.map((vendor) => (
                          <button key={vendor.id} type="button" className="rounded-full border border-[#2D7A65]/25 bg-white px-3 py-1.5 text-left text-xs font-bold text-[#17211D] shadow-sm hover:border-[#2D7A65]" onClick={() => toggleExistingVendor(vendor)}>
                            {vendor.name}
                            {hasValue(vendor.region) && <span className="ml-2 font-semibold text-slate-500">{vendor.region}</span>}
                            {editing?.type !== "assignment" && <X className="ml-2 inline h-3.5 w-3.5 text-slate-400" />}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="mb-2 text-xs font-black uppercase tracking-[0.14em] text-[#516259]">{form.vendor_region ? `Filtered for ${form.vendor_region}` : "All regions"}</p>
                    <div className="grid gap-3 md:grid-cols-2">
                      {providerVendorList.map((vendor) => {
                        const checked = form.vendor_ids.includes(vendor.id);
                        return (
                          <button key={vendor.id} type="button" className={`group rounded-xl border p-4 text-left transition hover:-translate-y-0.5 ${checked ? "border-[#2D7A65] bg-[#2D7A65]/10 shadow-sm" : "border-[#E2DFD7] bg-white hover:border-[#2D7A65] hover:bg-[#F7F1E5] hover:shadow-md"}`} onClick={() => toggleExistingVendor(vendor)}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex min-w-0 items-start gap-3">
                                <VendorCategoryBadge category={vendor.category} compact />
                                <div className="min-w-0">
                                  <p className="truncate font-semibold text-[#17211D]">{vendor.name}</p>
                                  <p className="text-sm capitalize text-slate-500">{[vendor.category, vendor.region].filter(hasValue).join(" · ")}</p>
                                </div>
                              </div>
                              <span className={`inline-flex h-7 min-w-20 items-center justify-center rounded-full px-3 text-xs font-bold ${checked ? "bg-[#2D7A65] text-white" : "bg-[#EBC253]/25 text-[#17211D]"}`}>
                                {checked ? "Selected" : "Select"}
                              </span>
                            </div>
                            {[csv(vendor.service_types), vendor.details].some(hasValue) && <p className="mt-2 text-sm text-slate-600">{[csv(vendor.service_types), vendor.details].find(hasValue)}</p>}
                            <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
                              {vendor.address && <span className="rounded-full bg-white px-2 py-1">{vendor.address}</span>}
                              {vendor.charges !== null && vendor.charges !== undefined && vendor.charges !== "" && <span className="rounded-full bg-white px-2 py-1">Charges: {vendor.charges}</span>}
                              {vendor.rating !== null && vendor.rating !== undefined && vendor.rating !== "" && <span className="rounded-full bg-white px-2 py-1">Rating: {vendor.rating}</span>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {providerVendorList.length === 0 && <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">No matching organization vendor found. Go back and create a new provider.</p>}
                  </div>
                </div>
              )}

              {step === 1 && form.source === "new" && (
                <div className="space-y-4">
                  <h3 className="text-lg font-black text-[#17211D]">Minimum provider details</h3>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Logistics category" required>
                      <select className={inputCls} value={form.category} onChange={(e) => changeService(e.target.value)}>
                        <option value="transport">Transportation</option>
                        <option value="accommodation">Accommodation</option>
                      </select>
                    </Field>
                    <Field label={form.category === "accommodation" ? "Property name" : "Provider name"} required><input className={inputCls} value={form.provider_name} onChange={(e) => setForm({ ...form, provider_name: e.target.value })} /></Field>
                    <Field label="Region"><input className={inputCls} value={form.provider_region} onChange={(e) => setForm({ ...form, provider_region: e.target.value })} /></Field>
                    {form.category === "transport" && <Field label="Vehicle types"><input className={inputCls} value={form.vehicle_details} onChange={(e) => setForm({ ...form, vehicle_details: e.target.value })} /></Field>}
                    {form.category === "accommodation" && <Field label="Property type"><input className={inputCls} value={form.accommodation_type} onChange={(e) => setForm({ ...form, accommodation_type: e.target.value })} /></Field>}
                    {form.category === "accommodation" && <Field label="Address"><input className={inputCls} value={form.provider_address} onChange={(e) => setForm({ ...form, provider_address: e.target.value })} /></Field>}
                    {form.category === "accommodation" && <Field label="Room types"><input className={inputCls} value={form.room_type} onChange={(e) => setForm({ ...form, room_type: e.target.value })} /></Field>}
                    <Field label="Contact phone"><input className={inputCls} value={form.provider_contact_phone} onChange={(e) => setForm({ ...form, provider_contact_phone: e.target.value })} /></Field>
                    <Field label="Website"><input className={inputCls} value={form.provider_website} onChange={(e) => setForm({ ...form, provider_website: e.target.value })} /></Field>
                    <Field label={form.category === "accommodation" ? "Amenities / notes" : "Notes"}><textarea className={inputCls + " min-h-20 sm:col-span-2"} value={form.provider_details} onChange={(e) => setForm({ ...form, provider_details: e.target.value })} /></Field>
                  </div>
                  {duplicateMatches.length > 0 && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                      <p className="font-semibold">Possible existing vendor found</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {duplicateMatches.map((vendor) => <Btn key={vendor.id} type="button" variant="secondary" onClick={() => applyDuplicateVendor(vendor)}>Use {vendor.name}</Btn>)}
                        <Btn type="button" variant="secondary" onClick={() => setForm({ ...form, create_separate_vendor: true })}>Create separate vendor</Btn>
                      </div>
                    </div>
                  )}
                  <label className="flex items-start gap-2 rounded-xl border border-[#E2DFD7] bg-white p-3 text-sm">
                    <input type="checkbox" className="mt-1" checked={!!form.add_to_org_vendor && canManageOrgVendors} disabled={!canManageOrgVendors} onChange={(e) => setForm({ ...form, add_to_org_vendor: e.target.checked })} />
                    <span><span className="block font-semibold">Save this provider to the organization vendor list for future trips?</span><span className="text-slate-500">{canManageOrgVendors ? `${form.provider_name || "This provider"} will become reusable for ${form.provider_region || "this region"}.` : "You do not have permission to create organization master vendors."}</span></span>
                  </label>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <h3 className="text-lg font-black text-[#17211D]">Assign to itinerary days</h3>
                      <p className="text-sm text-slate-500">At least one day is required. New vendors start with every day selected.</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Btn type="button" variant="secondary" onClick={suggestDays}>Use suggestion</Btn>
                      <Btn type="button" variant="secondary" onClick={selectAllDays}>Select all</Btn>
                    </div>
                  </div>
                  {days.length === 0 && <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Create at least one itinerary day before adding a vendor.</p>}
                  <div className="grid gap-2">
                    {days.map((day) => {
                      const checked = form.itinerary_day_ids.includes(day.id);
                      return (
                        <label key={day.id} className={`flex items-start gap-3 rounded-xl border p-3 text-sm ${checked ? "border-[#2D7A65] bg-[#2D7A65]/10" : "border-[#E2DFD7] bg-white"}`}>
                          <input type="checkbox" className="mt-1" checked={checked} onChange={() => toggleDay(day.id)} />
                          <span><span className="block font-semibold text-[#17211D]">{dayTitle(day)}</span><span className="text-slate-500">{day.date || ""} {day.summary || day.description || ""}</span></span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-black text-[#17211D]">Review {activeMeta.noun}</h3>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-xl border border-[#E2DFD7] bg-white p-4">
                      <p className="text-xs font-black uppercase text-[#516259]">Provider</p>
                      <p className="mt-1 font-semibold">{form.source === "existing" ? selectedVendors.map((vendor) => vendor.name).join(", ") || selectedVendor?.name || "Not set" : form.provider_name || "Not set"}</p>
                      <p className="text-sm text-slate-500">
                        {form.source === "existing"
                          ? `${selectedVendors.length || 1} organization vendor${(selectedVendors.length || 1) === 1 ? "" : "s"}`
                          : [form.provider_region, form.add_to_org_vendor && canManageOrgVendors ? "Save to master" : "Trip-only"].filter(hasValue).join(" · ")}
                      </p>
                    </div>
                    <div className="rounded-xl border border-[#E2DFD7] bg-white p-4">
                      <p className="text-xs font-black uppercase text-[#516259]">Assigned to</p>
                      <p className="mt-1 font-semibold">{dayList(form.itinerary_day_ids)}</p>
                      <p className="text-sm text-slate-500">Visibility: {form.public_visibility}</p>
                    </div>
                    {(hasValue(form.trip_price_minor) || hasValue(form.trip_specific_notes)) && <div className="rounded-xl border border-[#E2DFD7] bg-white p-4">
                      <p className="text-xs font-black uppercase text-[#516259]">{[hasValue(form.trip_price_minor) ? "Price" : "", hasValue(form.trip_specific_notes) ? "Notes" : ""].filter(Boolean).join(" & ")}</p>
                      {hasValue(form.trip_price_minor) && <p className="mt-1 text-sm">{form.trip_price_minor} {form.currency}</p>}
                      {hasValue(form.trip_specific_notes) && <p className="mt-1 whitespace-pre-wrap text-sm text-slate-500">{form.trip_specific_notes}</p>}
                    </div>}
                  </div>
                  {formWarnings.length > 0 && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                      <p className="font-semibold">Warnings</p>
                      <ul className="mt-2 list-disc pl-5">{formWarnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <SummitFormActions className="justify-between">
              <Btn type="button" variant="secondary" onClick={step === 0 ? closeWizard : back}>{step === 0 ? "Cancel" : "Back"}</Btn>
              {step < steps.length - 1 ? <Btn type="button" onClick={next}>Next</Btn> : <Btn type="submit"><Save className="h-4 w-4" /> Save {activeMeta.label}</Btn>}
            </SummitFormActions>
          </form>
        </div>,
        document.body,
      )}
    </div>
  );
}
